import { useState, useEffect } from 'preact/hooks';
import Router from 'preact-router';
import { ParentGate } from './components/ParentGate';
import { Home } from './pages/Home';
import { Create } from './pages/Create';
import { Read } from './pages/Read';
import { Library } from './pages/Library';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Settings } from './pages/Settings';
import { Success } from './pages/Success';
import { hasParentConsent, grantParentConsent } from './lib/consent';

// Pages that require parent consent
function ProtectedRoute({
  component: Component,
  onNeedsConsent,
  ...props
}: {
  component: typeof Create;
  onNeedsConsent: () => void;
  path: string;
  id?: string;
}) {
  const [hasConsent, setHasConsent] = useState<boolean | null>(null);

  useEffect(() => {
    const consent = hasParentConsent();
    setHasConsent(consent);
    if (!consent) {
      onNeedsConsent();
    }
  }, [onNeedsConsent]);

  if (hasConsent === null) {
    return null;
  }

  if (!hasConsent) {
    return null; // Will redirect via onNeedsConsent
  }

  return <Component {...props} />;
}

export function App() {
  const [showParentGate, setShowParentGate] = useState(false);
  const [redirectAfterConsent, setRedirectAfterConsent] = useState<string | null>(null);

  const handleNeedsConsent = (redirectTo?: string) => {
    setRedirectAfterConsent(redirectTo || '/create');
    setShowParentGate(true);
  };

  const handleVerified = () => {
    grantParentConsent();
    setShowParentGate(false);
    if (redirectAfterConsent) {
      window.location.href = redirectAfterConsent;
    }
  };

  if (showParentGate) {
    return <ParentGate onVerified={handleVerified} />;
  }

  return (
    <Router>
      <Home path="/" onStartCreate={() => handleNeedsConsent('/create')} />
      <ProtectedRoute
        path="/create"
        component={Create}
        onNeedsConsent={() => handleNeedsConsent('/create')}
      />
      <Read path="/read/:id" />
      <ProtectedRoute
        path="/library"
        component={Library}
        onNeedsConsent={() => handleNeedsConsent('/library')}
      />
      <Privacy path="/privacy" />
      <Terms path="/terms" />
      <Settings path="/settings" />
      <Success path="/success" />
    </Router>
  );
}
